import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import userRuleApi, { UserRuleDto } from 'src/api/user/userRuleApi';
import { StoreStatus } from 'src/shared/types/StoreStatus';
import { RootState } from 'src/store';

// # types
export interface UserRuleEntity extends UserRuleDto {
  dateStr: string;
}

export function toEntity(dto: UserRuleDto): UserRuleEntity {
  return {
    ...dto,
    dateStr: '',
  };
}

interface UserRuleEntityByCode {
  [code: string]: UserRuleEntity;
}
export interface UserRuleEntities {
  byCode: UserRuleEntityByCode;
  allCodes: string[];
}

interface UserRuleState {
  status: StoreStatus;
  entity: UserRuleEntity;
}

export const defaultUserRuelDto = (): UserRuleDto => {
  return {
    takeout: null,
    exceptScreen: null,
    printPolicy: null,
    primary: null,
    proxys: null,
    existApproval: true,
    isPrimaryExceed: null,
    existPenddingApproval: true,
    proxyApprover: true,
    approver: true,
  };
};

// # initial state
const initialState: UserRuleState = {
  status: StoreStatus.IDLE,
  entity: toEntity(defaultUserRuelDto()),
};

// # thunks
export const thunkLoadUserRules = createAsyncThunk('userRule/load', async () => {
  const data = await userRuleApi.getRule();
  return toEntity(data);
});

const userRuleSlice = createSlice({
  name: 'userRule',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(thunkLoadUserRules.pending, (state) => {
      state.status = StoreStatus.LOADING;
    });
    builder.addCase(thunkLoadUserRules.fulfilled, (state, { payload: userRule }) => {
      state.entity = toEntity(userRule);
      state.status = StoreStatus.DONE;
    });
    builder.addCase(thunkLoadUserRules.rejected, (state) => {
      state.status = StoreStatus.FAILED;
    });
  },
});

// # selectors
export const selectStoreStatus = (state: RootState): StoreStatus => {
  return state.userRule.status;
};
export const selectInitialized = (state: RootState): boolean => {
  return state.userRule.status === StoreStatus.DONE;
};
export const selectUserRuleEntity = (state: RootState): UserRuleEntity => state.userRule.entity;
export default userRuleSlice.reducer;

// 참고중...
// https://orizens.com/blog/how-to-not-have-a-mess-with-react-hooks-and-redux/
